import { observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IEntitlementProductPriceOverrideEntityAttributes extends IModelAttributes {
	price: number;
	entitlementProductId: string;
	entitlementProduct?: Models.EntitlementProductEntity | Models.IEntitlementProductEntityAttributes;
	userDisplayNameId: string;
	// The server projects userDisplayName as a flat displayName string on read; widened here so the entity
	// can hydrate a stub reference from either the standard nested object or the legacy string shape.
	userDisplayName?: Models.UserDisplayNameEntity | Models.IUserDisplayNameEntityAttributes | string;
}

@entity('EntitlementProductPriceOverrideEntity', 'Entitlement Product Price Override')
export default class EntitlementProductPriceOverrideEntity
	extends Model
	implements IEntitlementProductPriceOverrideEntityAttributes
{
	public static excludeFromCreate: string[] = [];
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EntitlementProductPriceOverrideEntity, number>()
	public price: number;

	@observable
	@attribute()
	public entitlementProductId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public entitlementProduct?: Models.EntitlementProductEntity;

	@observable
	@attribute()
	public userDisplayNameId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public userDisplayName?: Models.UserDisplayNameEntity;

	constructor(attributes?: Partial<IEntitlementProductPriceOverrideEntityAttributes>) {
		super(attributes);
		makeObservable(this);
		this.assignAttributes(attributes);
	}

	public assignAttributes(attributes?: Partial<IEntitlementProductPriceOverrideEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.entitlementProductId !== undefined) {
				this.entitlementProductId = attributes.entitlementProductId;
			}
			if (attributes.entitlementProduct !== undefined) {
				if (attributes.entitlementProduct === null) {
					this.entitlementProduct = undefined;
				} else if (attributes.entitlementProduct instanceof Models.EntitlementProductEntity) {
					this.entitlementProduct = attributes.entitlementProduct;
					this.entitlementProductId = attributes.entitlementProduct.id;
				} else {
					this.entitlementProduct = new Models.EntitlementProductEntity(attributes.entitlementProduct);
					this.entitlementProductId = this.entitlementProduct.id;
				}
			}
			if (attributes.userDisplayNameId !== undefined) {
				this.userDisplayNameId = attributes.userDisplayNameId;
			}
			if (attributes.userDisplayName !== undefined) {
				if (attributes.userDisplayName === null) {
					this.userDisplayName = undefined;
				} else if (attributes.userDisplayName instanceof Models.UserDisplayNameEntity) {
					this.userDisplayName = attributes.userDisplayName;
					this.userDisplayNameId = attributes.userDisplayName.id;
				} else if (typeof attributes.userDisplayName === 'string') {
					this.userDisplayName = new Models.UserDisplayNameEntity({
						id: this.userDisplayNameId,
						displayName: attributes.userDisplayName,
					});
				} else {
					this.userDisplayName = new Models.UserDisplayNameEntity(attributes.userDisplayName);
					this.userDisplayNameId = this.userDisplayName.id;
				}
			}
		}
	}
}
